import React, { lazy, Suspense } from 'react';

import type { BulkEditTarget } from './BulkEdit';
import { StatusDialog, AssignDialog, ScheduleDialog, NoteDialog } from './ListActionDialogParts';
import { DialogType, StatusOption } from './useListJobActions';
import { BulkRunFailure, BulkRunProgress } from '../../utils/bulkRun';

/**
 * Code-split: the bulk edit dialog pulls in `xlsx` for the template writer and
 * file parser, which is ~139 kB gzipped. Imported statically it landed in the
 * initial bundle, so everyone opening the Jobs list downloaded a spreadsheet
 * library whether or not they ever bulk-edited anything (main.js 1.01 → 1.16 MB).
 * It renders only while `activeDialog === 'bulkEdit'`, so this is a clean split.
 */
const BulkEditServiceDialog = lazy(() =>
  import('./BulkEdit').then((m) => ({ default: m.BulkEditServiceDialog }))
);

export interface ListActionDialogsProps {
  activeDialog: DialogType;
  targetIds: number[];
  /** Bulk edit needs each job's closed state, which plain ids can't carry. */
  bulkEditTargets?: BulkEditTarget[];
  onRefresh?: () => void;
  onClose: () => void;
  statusOptions: StatusOption[];
  statusLoading: boolean;
  onSubmitStatus: (code: string, notes: string) => Promise<void>;
  onSubmitAllocation: (nodeId: number, typeCode: string) => Promise<void>;
  onSubmitSchedule: (date: string) => Promise<void>;
  onSubmitNote: (note: string) => Promise<void>;
  submitting: boolean;
  lastError: string | null;
  isBulkRun?: boolean;
  /** Distinct service types among the selected jobs, for the status warning. */
  serviceTypeNames?: string[];
  progress?: BulkRunProgress | null;
  failures?: BulkRunFailure[];
}

/** Renders all action dialogs -- only the active one is visible. */
export const ListActionDialogs: React.FC<ListActionDialogsProps> = ({
  activeDialog,
  targetIds,
  bulkEditTargets,
  onRefresh,
  onClose,
  statusOptions,
  statusLoading,
  onSubmitStatus,
  onSubmitAllocation,
  onSubmitSchedule,
  onSubmitNote,
  submitting,
  lastError,
  isBulkRun,
  serviceTypeNames,
  progress,
  failures,
}) => {
  const isBulk = targetIds.length > 1;
  const count = targetIds.length;

  return (
    <>
      <StatusDialog
        open={activeDialog === 'status'}
        isBulk={isBulk}
        count={count}
        options={statusOptions}
        loading={statusLoading}
        submitting={submitting}
        bypassesInteractions={Boolean(isBulkRun)}
        serviceTypeNames={serviceTypeNames}
        lastError={activeDialog === 'status' ? lastError : null}
        progress={activeDialog === 'status' ? progress : null}
        failures={activeDialog === 'status' ? failures : []}
        onClose={onClose}
        onSubmit={onSubmitStatus}
      />
      <AssignDialog
        open={activeDialog === 'assign'}
        isBulk={isBulk}
        count={count}
        submitting={submitting}
        lastError={activeDialog === 'assign' ? lastError : null}
        progress={activeDialog === 'assign' ? progress : null}
        failures={activeDialog === 'assign' ? failures : []}
        onClose={onClose}
        onSubmit={onSubmitAllocation}
      />
      <ScheduleDialog
        open={activeDialog === 'schedule'}
        isBulk={isBulk}
        count={count}
        submitting={submitting}
        lastError={activeDialog === 'schedule' ? lastError : null}
        progress={activeDialog === 'schedule' ? progress : null}
        failures={activeDialog === 'schedule' ? failures : []}
        onClose={onClose}
        onSubmit={onSubmitSchedule}
      />
      <NoteDialog
        open={activeDialog === 'note'}
        isBulk={isBulk}
        count={count}
        submitting={submitting}
        lastError={activeDialog === 'note' ? lastError : null}
        progress={activeDialog === 'note' ? progress : null}
        failures={activeDialog === 'note' ? failures : []}
        onClose={onClose}
        onSubmit={onSubmitNote}
      />
      {activeDialog === 'bulkEdit' && (
        // Mounted only while active: the dialog fetches status options for its
        // targets on open, and keeping it mounted would probe on every list change.
        <Suspense fallback={null}>
          <BulkEditServiceDialog
            open
            targets={bulkEditTargets ?? targetIds.map((id) => ({ id }))}
            onClose={onClose}
            onRefresh={onRefresh}
          />
        </Suspense>
      )}
    </>
  );
};
